import { AlertTriangle, Bell, Info, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAdminNotifiche, type NotificaItem } from "@/hooks/useAdminShell";
import { cn } from "@/lib/utils";

function chiave(n: NotificaItem): string {
  return `${n.tipo}:${n.titolo}`;
}

const ICONA = {
  err: { Icon: AlertTriangle, cls: "bg-red-50 text-red-600" },
  warn: { Icon: AlertTriangle, cls: "bg-amber-50 text-amber-600" },
  info: { Icon: Info, cls: "bg-blue-50 text-blue-600" },
};

/** Campanella con contatore + dropdown delle notifiche operative. */
export function NotifichePanel() {
  const { data } = useAdminNotifiche();
  const navigate = useNavigate();
  const [aperto, setAperto] = useState(false);
  const [letti, setLetti] = useState<Set<string>>(new Set());
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAperto(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const notifiche = (data?.notifiche ?? []).filter((n) => !letti.has(chiave(n)));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        className="relative grid size-9 place-items-center rounded-md border bg-card text-slate-600 hover:bg-muted"
        aria-label="Notifiche"
      >
        <Bell className="size-[18px]" />
        {notifiche.length > 0 && (
          <span className="absolute -right-1.5 -top-1.5 grid size-[18px] place-items-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {notifiche.length}
          </span>
        )}
      </button>

      {aperto && (
        <div className="absolute right-0 top-11 z-40 w-80 overflow-hidden rounded-xl border bg-card shadow-lg">
          <div className="flex items-center justify-between border-b px-3.5 py-2.5 text-sm font-semibold">
            Notifiche
            {notifiche.length > 0 && (
              <button
                type="button"
                className="text-xs font-medium text-brand-700 hover:underline"
                onClick={() => setLetti(new Set((data?.notifiche ?? []).map(chiave)))}
              >
                Segna tutte lette
              </button>
            )}
          </div>
          {notifiche.length === 0 ? (
            <div className="px-3.5 py-6 text-center text-sm text-muted-foreground">
              Nessuna notifica.
            </div>
          ) : (
            notifiche.map((n) => {
              const { Icon, cls } = ICONA[n.severita];
              return (
                <button
                  key={chiave(n)}
                  type="button"
                  onClick={() => {
                    setAperto(false);
                    navigate(n.link);
                  }}
                  className="flex w-full items-start gap-2.5 border-b px-3.5 py-2.5 text-left last:border-b-0 hover:bg-muted/50"
                >
                  <span className={cn("grid size-7 shrink-0 place-items-center rounded-md", cls)}>
                    <Icon className="size-3.5" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-medium">{n.titolo}</span>
                    <span className="block text-xs text-muted-foreground">{n.dettaglio}</span>
                  </span>
                  <X
                    className="mt-0.5 size-3.5 shrink-0 text-slate-300 hover:text-slate-500"
                    onClick={(e) => {
                      e.stopPropagation();
                      setLetti((prev) => new Set(prev).add(chiave(n)));
                    }}
                  />
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
